import { useState } from 'react'

import { useDispatch } from './useDispatch'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { useTranslation } from 'react-i18next'

import {
  IncidentQueryResult,
  useFacilityGuardsQuery,
} from '@/graphql/generated/hooks'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'
import { getGuardOptions } from '@/utils/forms/optionBuilders'

import { MixpanelDataIProps } from '../types/types'

interface UseGuardDispatchIProps {
  incident?: IncidentQueryResult['data']['incident']
  mixpanelData?: MixpanelDataIProps
  onActionComplete?: () => void
}

export const useGuardDispatch = ({
  incident,
  mixpanelData,
  onActionComplete,
}: UseGuardDispatchIProps) => {
  const { t } = useTranslation()
  const { shouldEnableIncidentRadioGuards } = usePermissions()
  const { showSuccess, showError } = useToasts()
  const [isLoading, setIsLoading] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  // Guards must be explicitly selected — there is no "All guards" option
  // (ENG-32146), so the selection starts empty and dispatch stays disabled
  // until at least one guard is picked.
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const {
    audioFile,
    note,
    onNoteChange,
    onClearAudioFile,
    onGenerateAudioFile,
    dispatch,
  } = useDispatch({ incident })

  const facilityId = incident?.facility?.id
  const alreadyDispatchedGuardIds = incident?.dispatchedGuardIds || []

  const { data: guardsData, loading: isGuardsLoading } = useFacilityGuardsQuery(
    {
      skip: !facilityId,
      variables: { facilityId: facilityId! },
      fetchPolicy: 'network-only',
    }
  )

  const checkedInGuardOptions = getGuardOptions(guardsData)

  const guardsOptions = checkedInGuardOptions.filter(
    (opt) => !alreadyDispatchedGuardIds.includes(opt.value)
  )

  // Both lists are empty while the query is in flight, and also while it is
  // skipped waiting on the incident's facility — in neither case do we yet know
  // whether guards exist, so both count as "has guards" to keep the no-guards
  // alert from flashing before the real result lands.
  const hasAvailableGuards =
    !facilityId || isGuardsLoading || guardsOptions.length > 0

  // Dispatch consumes a guard for this incident, so it reads the filtered list
  // above; radio broadcasts to everyone checked in at the facility, and an
  // already-dispatched guard is still reachable by radio. Radio callers must
  // therefore gate on this unfiltered count, or the panel warns "no available
  // guards" the moment every guard has been dispatched (ENG-33571).
  const hasCheckedInGuards =
    !facilityId || isGuardsLoading || checkedInGuardOptions.length > 0

  const toggle = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const onClear = () => setSelectedIds([])

  const onCancel = () => {
    trackEvent(`${BUTTON_PRESS} Dispatch - Cancel Button`, mixpanelData)
    onActionComplete?.()
  }

  const isDispatchDisabled =
    !shouldEnableIncidentRadioGuards || selectedIds.length === 0 || isRecording

  const onDispatch = async () => {
    if (!shouldEnableIncidentRadioGuards || selectedIds.length === 0) return
    setIsLoading(true)
    try {
      await dispatch(selectedIds)
      trackEvent(`${BUTTON_PRESS} Dispatch Guards - Submit`, {
        ...mixpanelData,
        guard_ids: selectedIds,
      })
      onActionComplete?.()
      showSuccess(t('incidents:incident_dispatched'))
    } catch {
      showError()
    } finally {
      setIsLoading(false)
    }
  }

  return {
    audioFile,
    note,
    onNoteChange,
    onClearAudioFile,
    onGenerateAudioFile,
    isLoading,
    isRecording,
    setIsRecording,
    selectedIds,
    guardsOptions,
    toggle,
    onClear,
    onCancel,
    isDispatchDisabled,
    onDispatch,
    hasAvailableGuards,
    hasCheckedInGuards,
  }
}
